import { makeMergedPersisted } from '@trimble/solidjs-utils';
import { createContext, JSX, useContext } from 'solid-js';
import { createStore, SetStoreFunction } from 'solid-js/store';
import { PaneLayouts } from '../models/pane-manager-models';
import lodash from 'lodash';
import { isPaneLayoutValid } from './pane-layout-validator';

export interface PaneContextState<T> {
  layouts: PaneLayouts<T>;
  setLayouts: SetStoreFunction<PaneLayouts<T>>;
}

export interface CreatePaneContextConfig<T> {
  /** Default layout configuration. Will be deep-cloned internally. */
  defaultLayouts: PaneLayouts<T>;
  /** localStorage key for persistence. */
  persistenceKey: string;
}

/**
 * Creates a typed pane context provider and hook pair.
 *
 * @example
 * ```ts
 * const { PaneContextProvider, usePaneContext } = createPaneContext<ViewMode>({
 *   defaultLayouts: PANE_LAYOUT,
 *   persistenceKey: 'pane_layout',
 * });
 * ```
 */

export function createPaneContext<T>(config: CreatePaneContextConfig<T>): {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  PaneContextProvider(props: { children: JSX.Element }): JSX.Element;
  usePaneContext(): PaneContextState<T>;
} {
  const PaneContext = createContext<PaneContextState<T>>();

  function PaneContextProvider(props: { children: JSX.Element }): JSX.Element {
    // Clone because an original object will be changed according to value from the localStorage.
    const defaultPaneLayout = lodash.cloneDeep(config.defaultLayouts);

    const [layouts, setLayouts] = makeMergedPersisted(
      // eslint-disable-next-line solid/reactivity
      createStore<PaneLayouts<T>>(defaultPaneLayout),
      {
        name: config.persistenceKey,
        validate: (value) => isPaneLayoutValid(defaultPaneLayout, value),
      }
    );

    return (
      <PaneContext.Provider
        value={{
          layouts,
          setLayouts,
        }}
      >
        {props.children}
      </PaneContext.Provider>
    );
  }

  function usePaneContext(): PaneContextState<T> {
    const context = useContext(PaneContext);
    if (!context) {
      throw new Error(
        'usePaneContext must be used within a PaneContextProvider'
      );
    }
    return context;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  return { PaneContextProvider, usePaneContext };
}
